import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import "../styles/CaseStudyPage.css";
import { useLanguage } from "../contexts/LanguageContext";
import { usePageMeta } from "../utils/usePageMeta";
import { track } from "../utils/analytics";
import Header from "../components/Header";
import ContactIcons from "../components/ContactIcons";

const CaseStudyPage = () => {
  const { slug } = useParams();
  const { language, text } = useLanguage();
  const isHeb = language === "he";
  const detail = text.portfolio.detail;
  const item = text.portfolio.items.find((i) => i.slug === slug);
  const others = text.portfolio.items.filter((i) => i.slug !== slug).slice(0, 3);

  usePageMeta({
    title: item ? `${item.title} | NKR case study` : "NKR — Case study",
    description: item ? item.solution : undefined,
  });

  useEffect(() => {
    track("case_study_view", { slug });
    window.scrollTo(0, 0);
  }, [slug]);

  if (!item) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <div className="header-shell">
        <Header />
      </div>

      <main className="case-page" dir={isHeb ? "rtl" : "ltr"}>
        <Link to="/" className="case-back">
          {detail.backToHome}
        </Link>

        <header className="case-header">
          <span className="case-tag">{item.tag}</span>
          <h1 className="case-page-title">{item.title}</h1>
        </header>

        <div className="case-mock" aria-hidden="true">
          <div className="case-mock-bar">
            <span className="hero-window-dot dot-r" />
            <span className="hero-window-dot dot-y" />
            <span className="hero-window-dot dot-g" />
            <span className="case-mock-url">nkr.dev/work/{slug}</span>
          </div>
          <div className="case-mock-body">
            <span className="case-mock-emoji">📊</span>
          </div>
        </div>

        <section className="case-section">
          <h2 className="case-label">{detail.problemLabel}</h2>
          <p className="case-prose">{item.problem}</p>
        </section>

        <section className="case-section">
          <h2 className="case-label">{detail.solutionLabel}</h2>
          <p className="case-prose">{item.solution}</p>
        </section>

        <section className="case-section case-result-block">
          <h2 className="case-label case-label-result">{detail.resultLabel}</h2>
          <p className="case-result-text">{item.result}</p>
        </section>

        <section className="case-section">
          <h2 className="case-label">{detail.stackLabel}</h2>
          <div className="case-page-stack">
            {item.stack.map((s) => (
              <span key={s} className="case-stack-chip">{s}</span>
            ))}
          </div>
        </section>

        <section className="case-cta-card">
          <h2>{detail.ctaTitle}</h2>
          <p>{detail.ctaDesc}</p>
          <Link
            to="/#contact"
            className="btn btn-primary"
            onClick={() => track("case_study_cta", { slug })}
          >
            {detail.ctaButton}
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </section>

        <section className="case-related">
          <h2 className="case-label">{detail.relatedTitle}</h2>
          <div className="case-related-grid">
            {others.map((o) => (
              <Link key={o.slug} to={`/work/${o.slug}`} className="case-related-card">
                <span className="case-tag">{o.tag}</span>
                <h3>{o.title}</h3>
                <span className="case-related-result">{o.result}</span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <ContactIcons />
        <p>
          © {new Date().getFullYear()} {text.brand} · {text.footer}
        </p>
      </footer>
    </>
  );
};

export default CaseStudyPage;
